import { useKDSStore } from '../stores/kdsStore';
import TicketCard from './TicketCard';
import { ChefHat } from 'lucide-react';
import clsx from 'clsx';

interface TicketGridProps {
  currentTime: Date;
}

export default function TicketGrid({ currentTime }: TicketGridProps) {
  const { getFilteredTickets, settings } = useKDSStore();
  const tickets = getFilteredTickets();

  // Sort tickets by age (oldest first) and priority
  const sortedTickets = [...tickets].sort((a, b) => {
    // VIP and rush orders first
    if (a.priority === 'vip' && b.priority !== 'vip') return -1;
    if (b.priority === 'vip' && a.priority !== 'vip') return 1;
    if (a.priority === 'rush' && b.priority !== 'rush') return -1;
    if (b.priority === 'rush' && a.priority !== 'rush') return 1;

    // Then by sent time (oldest first)
    return new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime();
  });

  if (tickets.length === 0) {
    return (
      // Scrollable + 1px-taller inner = iOS rubber-band bounce even when empty
      <div
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain safe-x"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div className="min-h-[calc(100%+1px)] flex items-center justify-center text-slate-500">
          <div className="text-center px-4">
            <ChefHat className="w-20 h-20 mx-auto mb-4 opacity-30" />
            <p className="text-2xl font-semibold">No Active Tickets</p>
            <p className="text-base mt-2 text-slate-400">Waiting for orders...</p>
          </div>
        </div>
      </div>
    );
  }

  // Responsive column count based on screen size and settings
  const getGridClasses = () => {
    const cols = settings.columnsCount;
    // Mobile: max 2 columns, Tablet: max 3, Desktop: use setting
    return clsx(
      'grid-cols-1',
      'sm:grid-cols-2',
      cols >= 3 && 'md:grid-cols-3',
      cols >= 4 && 'lg:grid-cols-4',
      cols >= 5 && 'xl:grid-cols-5',
      cols >= 6 && '2xl:grid-cols-6'
    );
  };

  return (
    // Scroll container wraps the grid; the 1px-taller inner keeps iOS
    // rubber-band bounce alive even when all tickets fit on screen
    <div
      className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      <div
        className={clsx(
          'min-h-[calc(100%+1px)] content-start p-2 sm:p-4 grid gap-3 sm:gap-4 safe-x',
          getGridClasses()
        )}
        style={{ gridAutoRows: 'min-content' }}
      >
        {sortedTickets.map((ticket) => (
          <TicketCard key={ticket.id} ticket={ticket} currentTime={currentTime} />
        ))}
      </div>
    </div>
  );
}
